function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
/** @jsxRuntime classic */

/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import Rodal from 'rodal';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useMutation, useQueryCache } from 'react-query';
import { useTheme } from 'emotion-theming';
import { isMobile as isAMobile } from 'react-device-detect';
import { home, migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from '../Colors';
import { client } from '../../utils/api-client';
import useMediaQuery from '../../hooks/use-media-query';
import { BallonIcon, CloseIcon, DownloadAltIcon, ExclamationTriangleIcon, MailIcon, PencilCircleIcon, SaveCircleIcon, TrafficConeIcon, CircleLockIcon, CircleUnlockIcon, CheckCircleIcon } from '../../icon';
import AlertRequiredFields from '../advanced/procedure/sections/shared/AlertRequiredFields';
import Button from '../Button';
import FormError from '../FormError';
import ButtonSelector from '../forms/inputs/ButtonSelector';
import TextArea from '../forms/inputs/TextArea';
import ScrollWrapper from '../ScrollWrapper';
import { NotesInfoDocument } from '../cards/notes-info/NotesInfo';
import MultiFileUploader from '../forms/inputs/MultiFileUploader';
import { mediaQuery, reducer, handleDownloadSingleFile, dataToArray, cloneElement } from '../../utils';
import { DivRow, DivColumn } from './InputsUI';
import { useWizard } from '../navigations/Wizard';
import SpinnerSmall from '../SpinnerSmall';
import { FileUploaderProvider, Uploader } from '../forms/inputs/FileUploader';
import { SimpleCheckBox } from '../forms/inputs/Checkbox';
import { InputText, FormDiv, FormLabel } from '../advanced/procedure/sections/dgme/components/InputsUI';
import { useIdentificationsTypes } from '../../queries';
import { Typography } from './shared';
const themes = {
  home: {
    alpha: home.fireEngineRed,
    beta: home.white,
    gamma: home.cerulean,
    delta: home.cerulean,
    epsilon: home.prussianBlue,
    dseta: home.cerulean,
    eta: home.cerulean,
    zeta: home.cerulean,
    iota: home.cerulean,
    kappa: home.white
  },
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.fireEngineRed,
    beta: migratoryWebCitizen.white,
    gamma: migratoryWebCitizen.cerulean,
    delta: migratoryWebCitizen.cerulean,
    epsilon: migratoryWebCitizen.prussianBlue,
    dseta: migratoryWebCitizen.cerulean,
    eta: migratoryWebCitizen.cerulean,
    zeta: migratoryWebCitizen.cerulean,
    iota: migratoryWebCitizen.deepCerulean,
    kappa: migratoryWebCitizen.white
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.fireEngineRed,
    beta: sanitarianRegistersWeb.white,
    gamma: sanitarianRegistersWeb.cerulean,
    delta: sanitarianRegistersWeb.cerulean,
    epsilon: sanitarianRegistersWeb.prussianBlue,
    dseta: sanitarianRegistersWeb.deepCerulean,
    eta: sanitarianRegistersWeb.cerulean,
    zeta: sanitarianRegistersWeb.cerulean,
    iota: sanitarianRegistersWeb.deepCerulean,
    kappa: sanitarianRegistersWeb.blackPearl
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.mantis,
    beta: phytosanitaryWeb.sherpaBlue,
    gamma: phytosanitaryWeb.allports,
    delta: phytosanitaryWeb.allports,
    epsilon: phytosanitaryWeb.allports,
    dseta: phytosanitaryWeb.mantis,
    eta: phytosanitaryWeb.mantis,
    zeta: phytosanitaryWeb.lightSeaGreen,
    iota: phytosanitaryWeb.lightSeaGreen,
    kappa: phytosanitaryWeb.white
  },
  tdCarWeb: {
    alpha: tdCarWeb.fireEngineRed,
    beta: tdCarWeb.white,
    gamma: tdCarWeb.cerulean,
    delta: tdCarWeb.cerulean,
    epsilon: tdCarWeb.prussianBlue,
    dseta: tdCarWeb.cerulean,
    eta: tdCarWeb.cerulean,
    zeta: tdCarWeb.cerulean,
    iota: tdCarWeb.deepCerulean,
    kappa: tdCarWeb.white
  }
};

const modalCustomStyle = ({
  isMobile,
  width = 781,
  height = 255,
  borderRadius = 20
}) => ({
  width: isMobile ? '80%' : width,
  height: isMobile ? 'auto' : height,
  borderRadius: isMobile ? 8 : borderRadius,
  transform: isMobile ? 'translate(-50%, -50%)' : 'unset',
  top: isMobile ? '50%' : 0,
  left: isMobile ? '50%' : 0,
  right: isMobile ? 'unset' : 0,
  bottom: isMobile ? 'unset' : 0
});
/**
 * Este componente se usa para el nuevo wizard
 * el de useWizard.
 *
 * @export
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onCloseModal Function triggered when close the modal.
 * @param {boolean} isPending If true the modal is doing an action.
 * @param {function} onSave Function triggered when accept the modal action.
 */


export function SaveAndExitModalAlt({
  isVisible,
  onCloseModal,
  isPending,
  onSave
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(SaveCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Continuar después')), jsx("p", {
    className: "modals-description"
  }, t('Podrá encontrar este trámite en el histórico de trámites')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    disabled: isPending,
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    className: "button blue",
    onClick: onCloseModal
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    disabled: isPending,
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onSave
  }, t('Salir del trámite'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a save and exit modal from procedures on mobile size screen.
 * @param {function} onSaveAndExitInMobile Triggered for save the step on procedures.
 */

export function SaveAndExitModal({
  onSaveAndExitInMobile
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    status,
    dispatch,
    isSaveAndExitModalVisible
  } = useWizard();
  const isPending = ['isPendingSaveAndNavigate', 'saveAndExit'].includes(status);
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isSaveAndExitModalVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(SaveCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Continuar después')), jsx("p", {
    className: "modals-description"
  }, t('Podrá encontrar este trámite en el histórico de trámites')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    disabled: isPending,
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    className: "button blue",
    onClick: () => {
      dispatch({
        type: 'saveAndExitModal',
        visible: false
      });
    }
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    disabled: isPending,
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => {
      if (onSaveAndExitInMobile) {
        onSaveAndExitInMobile();
        return;
      }

      dispatch({
        type: 'saveAndExit'
      });
    }
  }, t('Salir del trámite'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for user informed consent.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal.
 * @param {function} callback Function triggered when the modal is closed.
 */

export function UserInformedConsentModal({
  isVisible,
  setIsVisible,
  callback
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation(); // const { status, dispatch } = useWizard()
  // const isPending = ['isPendingSaveAndNavigate', 'saveAndExit'].includes(status)

  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Constancia de consentimiento informado')), jsx("p", {
    className: "modals-description"
  }, t('Para poder continuar con el trámite debe seleccionar que está de acuerdo con la Constancia[ext]')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true // disabled={isPending}
    ,
    className: "button blue upper",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => {
      if (callback) {
        callback({});
        return;
      }

      history.push('/');
    }
  }, t('Salir del trámite')), jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    } // disabled={isPending}
    ,
    onClick: () => {
      setIsVisible(false);
    }
  }, t('Volver al consentimiento')))));
}
/**
 * This component represents a modal for send a procedure status
 * @param {boolean} isModalVisible Determines if the modal is visible or not.
 * @param {function} setIsModalVisible Function triggered when close the modal.
 * @param {boolean} wasProcedureSent Determines if the procedure was send.
 * @param {boolean} isDetailView Determines if the user can se de detail.
 */

export function SendProcedureStatusModal({
  isModalVisible,
  setIsModalVisible,
  wasProcedureSent,
  isDetailView = false,
  detailMessage = ''
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  let modalTitle = '';
  let modalDescription = '';
  let detail = '';

  if (isDetailView && wasProcedureSent) {
    modalTitle = t('Trámite enviado a revisión');
    modalDescription = t('Su trámite ha sido enviado a revisión Si desea revisarlo nuevamente puede encontrarlo en el Histórico de trámites');
  } else if (isDetailView) {
    modalTitle = t('Atención');
    modalDescription = t('En este momento no es posible enviar el trámite a revisión Por favor, inténtelo más tarde');
  } else {
    modalTitle = t('No se puede enviar el trámite en este momento');
    modalDescription = t('Por favor intentelo nuevamente O, si lo prefiere, puede accesarlo más tarde en la sección de Histórico de trámites y volver a intentarlo con el botón “Enviar a revisión”');
  }

  detail = `${detailMessage.includes('Dekra') || detailMessage.includes('ITV') ? 'El servicio de ITV no responde.' : ''}`;
  return jsx(Rodal, {
    visible: isModalVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    },
    css: isDetailView ? {} : {
      fontSize: '22px !important'
    }
  }, modalTitle), jsx("p", {
    className: "modals-description"
  }, modalDescription), jsx("p", {
    className: "modals-description"
  }, detail))), jsx("div", {
    className: "button-container"
  }, isDetailView ? jsx(Button, {
    color: "fireEngineRed",
    style: {
      width: 238,
      height: 45,
      color: '#FFFFFF',
      borderRadius: 6,
      padding: 0,
      border: 'none',
      textTransform: 'uppercase'
    },
    onClick: () => setIsModalVisible(false)
  }, t('Aceptar')) : jsx(React.Fragment, null, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => history.push('/procedures')
  }, t('Ir a histórico de trámites')), jsx(Button, {
    color: "fireEngineRed",
    style: {
      width: 238,
      height: 45,
      color: themes[theme].beta,
      background: themes[theme].alpha,
      borderRadius: 6,
      padding: 0,
      border: 'none',
      textTransform: 'uppercase'
    },
    onClick: () => setIsModalVisible(false)
  }, t('Volver al trámite'))))));
}
/**
 * This component represents a modal for incorrect identification.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal.
 * @param {string} title Modal title.
 */

export function IncorrectIdentificationModal({
  isVisible,
  setIsVisible,
  title = 'Ingresar otro número de identificación'
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 780,
    height: 250,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 30
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t(title)), jsx("p", {
    className: "modals-description"
  }, t('Al seleccionar trámite debe ingresar datos que no correspondan a su información personal')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(false)
  }, t('Aceptar')))));
}
/**
 * This component represents a modal when the procedure can not be edited.
 * @param {boolean} isModalVisible Determines if the modal is visible or not.
 * @param {function} proceedToPay Function to proceed to pay.
 * @param {function} setIsModalVisible Function triggered when close the modal.
 * @param {boolean} isPending Determines if there are a proccess pending.
 */

export function ProcedureCannotEditedModal({
  isModalVisible,
  proceedToPay,
  setIsModalVisible,
  isPending = false
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isModalVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    customStyles: modalCustomStyle({
      isMobile
    }),
    onClose: () => {}
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Atención')), jsx("p", {
    className: "modals-description"
  }, t('Una vez realizado el pago, no podrá volver a editar la información de este trámite[ext]')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => setIsModalVisible(false)
  }, t('Volver a confirmación')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: proceedToPay
  }, t('Continuar a pago'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for filiation warning.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal.
 * @param {string} modalTitle Modal title.
 * @param {string} modalDescription Modal description.
 * @param {function} callback Function triggered when accept the modal action.
 */

export function FiliationWarningsModal({
  isVisible,
  setIsVisible,
  modalTitle,
  modalDescription,
  callback
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 30
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, modalTitle), jsx("p", {
    className: "modals-description"
  }, modalDescription))), jsx("div", {
    className: "button-container"
  }, jsx(React.Fragment, null, jsx(Button, {
    outline: true,
    size: "md-icon",
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: setIsVisible
  }, t('Volver')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: () => {
      if (callback) {
        callback({});
        return;
      }

      history.push('/');
    }
  }, t('Salir del trámite'))))));
}
/**
 * This component represents a modal for search filiation procedure warning.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} modalType Modal type.
 * @param {string} modalTitle Modal title.
 * @param {string} modalDescription Modal description.
 * @param {function} callback Function triggered when accept the modal action.
 */

export function SearchFiliationWarningsModal({
  isVisible,
  setIsVisible,
  modalType,
  modalTitle,
  modalDescription,
  callback
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 30
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, modalTitle), jsx("p", {
    className: "modals-description"
  }, modalDescription))), jsx("div", {
    className: "button-container"
  }, ['record', 'alert'].includes(modalType) ? jsx(React.Fragment, null, jsx(Button, {
    outline: true,
    size: "md-icon",
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: setIsVisible
  }, t('Cancelar')), modalType !== 'alert' ? jsx(Button, {
    size: "md-icon",
    className: "button red",
    spanStyle: {
      fontSize: 14
    },
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(true)
  }, t('Continuar trámite "Para mí"')) : jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: () => {
      if (callback) {
        callback({});
        return;
      }

      history.push('/procedures/28/information');
    }
  }, t('Ir al trámite de filiación'))) : jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: setIsVisible
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for deteling a procedure.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} proceedToDelete Deletes the procedure.
 * @param {string} isDeleting Determines if the delete is completed or not.
 */

export function DeleteProcedureWarningModal({
  isVisible,
  setIsVisible,
  proceedToDelete,
  isDeleting
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 255
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Eliminar trámite')), jsx("p", {
    className: "modals-description"
  }, t('¿Está seguro que desea eliminar el trámite? Se perderá toda la información ingresada y el avance del trámite hasta ahora')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    disabled: isDeleting,
    onClick: setIsVisible
  }, t('Cancelar'), isDeleting ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    disabled: isDeleting,
    onClick: proceedToDelete
  }, t('Eliminar'), isDeleting ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for deteling a person.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} proceedToDelete Deletes the procedure.
 * @param {string} isDeleting Determines if the delete is completed or not.
 */

export function DeletePersonWarningModal({
  isVisible,
  setIsVisible,
  proceedToDelete,
  isDeleting,
  role
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 255
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, role === 'notary' ? t('Eliminar notario') : t('Eliminar autorizado')), jsx("p", {
    className: "modals-description"
  }, t('Está seguro que desea eliminar este autorizado Al eliminarlo no podrá recuperar la información')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    disabled: isDeleting,
    onClick: setIsVisible
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    disabled: isDeleting,
    onClick: proceedToDelete
  }, t('Eliminar'), isDeleting ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for deteling a person.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} proceedToDelete Deletes the procedure.
 * @param {string} isDeleting Determines if the delete is completed or not.
 */

export function LegalRepresentativeWarning({
  isVisible,
  setIsVisible,
  navigateToBuyLegalStatus
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 255
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Es necesario ser representante legal')), jsx("p", {
    className: "modals-description"
  }, t('Si usted no se encuentra registrado como Representante Legal de la empresa ante el Registro Nacional su trámite no podrá ser completado Si desea comprar la personería jurídica puede hacerlo inmediatamente')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: setIsVisible
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      width: 280,
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: navigateToBuyLegalStatus
  }, t('Comprar Personería jurídica')))));
}
/**
 * This component represents a modal for deteleting a document.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} removeFile Deletes the document.
 * @param {boolean} isPendingDeleteFile Determines if the delete is completed or not.
 */

export function DeleteDocumentWarningModal({
  isVisible,
  setIsVisible,
  removeFile,
  isPendingDeleteFile
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 255
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Eliminar documento')), jsx("p", {
    className: "modals-description"
  }, t('¿Está seguro que desea eliminar este documento? Una vez eliminado no podrá recuperarlo')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    disabled: isPendingDeleteFile,
    onClick: () => setIsVisible(false)
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    disabled: isPendingDeleteFile,
    onClick: removeFile
  }, t('Eliminar'), isPendingDeleteFile ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for signature info by theme.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} logo Logo src.
 * @param {function} withoutDigitalSignature Stops creating an account.
 * @param {function} withDigitalSignature Create an account.
 * @param {string} cancel Cancel text.
 */

export function SignatureInfoByThemeModal({
  isVisible,
  setIsVisible,
  logo,
  withoutDigitalSignature,
  withDigitalSignature,
  cancel
}) {
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 830,
    height: 272,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 830,
      height: 272
    })
  }, jsx("div", {
    css: [styles, {
      padding: '28px 25px 11px 29px'
    }]
  }, jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(CloseIcon, {
    css: {
      width: 20,
      height: 20,
      cursor: 'pointer',
      color: themes[theme].beta
    },
    onClick: setIsVisible
  })), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 0
    }
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("img", {
    src: logo,
    className: "signature-logo",
    alt: "Firma logo"
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].beta
    }
  }, t('crear cuenta utilizando firma digital')), jsx("p", {
    className: "modals-description",
    style: {
      color: themes[theme].gamma
    }
  }, t('Para crear cuenta en la plataforma del Servicio Fitosanitario del Estado es necesario contar con Firma Digital')), isMobile ? jsx("p", {
    className: "modals-description"
  }, t('Sin embargo, para usar Firma Digital, es necesario utilizar la versión web de escritorio de la plataforma del Servicio Fitosanitario del Estado, por favor, ingrese desde una computadora')) : jsx("p", {
    className: "modals-description",
    style: {
      color: themes[theme].gamma
    }
  }, t('¿Desea crear su cuenta utilizando Firma Digital?')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    color: "fireEngineRed",
    size: "md-icon",
    outline: true,
    className: "button red2",
    style: {
      color: themes[theme].alpha,
      borderColor: themes[theme].alpha,
      width: 185
    },
    onClick: withoutDigitalSignature
  }, cancel ? t(cancel) : t('Cancelar')), !isMobile && jsx(Button, {
    color: "cerulean",
    size: "md-icon",
    className: "button blue2",
    style: {
      color: themes[theme].epsilon,
      backgroundColor: themes[theme].dseta
    },
    onClick: withDigitalSignature,
    disabled: isMobile
  }, t('Sí, crear cuenta')))));
}
/**
 * This component represents a modal for digital signature, on create account process.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} logo Logo src.
 * @param {function} withoutDigitalSignature Stops creating an account.
 * @param {function} withDigitalSignature Create an account.
 */

export function SignatureInfoModal({
  isVisible,
  setIsVisible,
  logo,
  withoutDigitalSignature,
  withDigitalSignature
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 830,
    height: 272,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 830,
      height: 290
    })
  }, jsx("div", {
    css: [styles, {
      padding: '28px 25px 11px 29px'
    }]
  }, jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(CloseIcon, {
    css: {
      width: 20,
      height: 20,
      cursor: 'pointer',
      color: themes[theme].alpha
    },
    onClick: setIsVisible
  })), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 0
    }
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("img", {
    src: logo,
    className: "signature-logo",
    alt: "Firma logo"
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].delta
    }
  }, t('crear cuenta utilizando firma digital')), jsx("p", {
    className: "modals-description"
  }, t('Estimado usuario, si usted cuenta con una Firma Digital válida en Costa Rica, puede utilizarla para crear su cuenta en Trámite ¡YA!')), isAMobile ? jsx("p", {
    className: "modals-description"
  }, t('Sin embargo, para usar Firma Digital, es necesario utilizar la versión web de escritorio de Tramite ¡YA!, por favor, ingrese desde una computadora')) : jsx("p", {
    className: "modals-description"
  }, t('¿Desea crear su cuenta utilizando Firma Digital?')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button red2",
    style: {
      background: themes[theme].beta,
      color: themes[theme].alpha,
      border: `1px solid ${themes[theme].alpha}`
    },
    onClick: withoutDigitalSignature
  }, isAMobile ? t('Crear cuenta sin firma digital') : t('No, crear cuenta sin firma digital')), !isAMobile && jsx(Button, {
    size: "md-icon",
    className: "button blue2",
    style: {
      background: themes[theme].dseta,
      color: themes[theme].beta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: withDigitalSignature
  }, t('Sí')))));
}
/**
 * This component represents a modal for signature DNN .
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} logo Logo src.
 * @param {function} cancel Cancel action.
 * @param {function} accept Create an account.
 */

export function SignatureInfoDNNModal({
  isVisible,
  setIsVisible,
  logo,
  cancel,
  accept
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 830,
    height: 272,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 830,
      height: 272
    })
  }, jsx("div", {
    css: styles,
    style: isMobile ? {
      padding: 20
    } : {
      padding: '28px 25px 11px 29px'
    }
  }, jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(CloseIcon, {
    css: {
      width: 20,
      height: 20,
      cursor: 'pointer',
      color: themes[theme].alpha
    },
    onClick: setIsVisible
  })), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 0
    }
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("img", {
    src: logo,
    className: "signature-logo",
    alt: "Firma logo"
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].delta
    }
  }, t('crear cuenta utilizando firma digital')), jsx("p", {
    className: "modals-description"
  }, t('Su número de identificación corresponde a un notario activo ante la DNN Para poder continuar con la creación de la cuenta debe realizarlo con firma digital')), isMobile ? jsx("p", {
    className: "modals-description"
  }, t('Sin embargo, para usar Firma Digital, es necesario utilizar la versión web de escritorio de Tramite ¡YA!, por favor, ingrese desde una computadora')) : jsx("p", {
    className: "modals-description"
  }, t('¿Desea crear su cuenta utilizando Firma Digital?')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button red2",
    style: {
      background: themes[theme].beta,
      color: themes[theme].alpha,
      border: `1px solid ${themes[theme].alpha}`
    },
    onClick: cancel
  }, t('Cancelar')), !isMobile && jsx(Button, {
    size: "md-icon",
    className: "button blue2",
    style: {
      background: themes[theme].dseta,
      color: themes[theme].beta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: accept,
    disabled: isMobile
  }, t('Sí, crear cuenta')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} redirectToSignUp Redirects to sing up.
 */

export function UserNotExistsModal({
  isVisible,
  setIsVisible,
  redirectToSignUp
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('El usuario no se encuentra registrado')), jsx("p", {
    className: "modals-description"
  }, t('error_firma_login')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: setIsVisible
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: redirectToSignUp
  }, t('Ir a registro')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onClick Modal action.
 */

export function DeleteAutorizedPersonModal({
  isVisible,
  onClick,
  personRole
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, personRole === 'notary' ? t('Notario eliminado') : t('Autorizado eliminado')), jsx("p", {
    className: "modals-description"
  }, t('El autorizado seleccionado fue eliminado exitosamente')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onClick
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onClick Modal action.
 */

export function PersonNotRegistered({
  isVisible,
  onClick
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('La persona no se encuentra registrada')), jsx("p", {
    className: "modals-description"
  }, t('El número de identificación indicado no pertenece a ninguna persona registrada en la plataforma por favor revise el número de identificación')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onClick
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onClick Modal action.
 */

export function PersonAlreadyAuthorized({
  isVisible,
  onClick
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('La persona ya se encuentra agregada')), jsx("p", {
    className: "modals-description"
  }, t('El número de identificación que ingresó pertenece a una persona que ya fue agregada a este expediente'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: onClick
  }, t('Volver')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => history.push('/people')
  }, t('Ir al inicio')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onClick Modal action.
 */

export function AddAutorizedPersonModal({
  isVisible,
  onClick,
  personRole
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Nuevo'), " ", t(personRole)), jsx("p", {
    className: "modals-description"
  }, personRole === 'notary' ? t('El registro del nuevo notario fue creado exitosamente.') : t('El registro del nuevo Autorizado fue creado exitosamente')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onClick
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for not found user.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} onClick Modal action.
 */

export function ModifyPersonModal({
  isVisible,
  onClick
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 52
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Información guardada')), jsx("p", {
    className: "modals-description"
  }, t('Se han modificado los datos exitosamente')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onClick
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for change the profile picture.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {*} photoUploaded Photo uploaded.
 * @param {function} deleteFileFromCDN Deletes the file from the CDN.
 * @param {object} FileUploadRef Ref to fileUploader.
 * @param {function} clearModalContent Cancel function.
 * @param {boolean} isPending Determines if the photo is changed or not.
 * @param {function} updatePhoto Updates the photo.
 * @param {boolean} isDisabledUpdateButton Determines if the update button is enable or disable.
 */

export function ChangeProfilePictureModal({
  isVisible,
  fileName,
  photoUploaded,
  deleteFileFromCDN,
  FileUploadRef,
  clearModalContent,
  isPending,
  updatePhoto,
  isDisabledUpdateButton
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 728,
    height: 420,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 728,
      height: 420
    })
  }, jsx("div", {
    css: [styles, {
      padding: '38px 20px 25px 23px'
    }]
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].delta
    }
  }, t('Actualizar fotografía')), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "column-profile",
    css: {
      justifyContent: 'center'
    }
  }, jsx("span", {
    className: "modals-description"
  }, t('Suba una nueva fotografía desde su computador'), ":")), jsx(DivColumn, {
    className: "column-profile",
    css: {
      alignItems: 'flex-end'
    }
  }, jsx(FileUploaderProvider, {
    onFinish: photoUploaded,
    serverPath: `${process.env.REACT_APP_CDN_URL}/upload`,
    t: t,
    onFinishDelete: () => deleteFileFromCDN(fileName)
  }, jsx(Uploader, {
    accept: ['.jpeg,.png,.jpg'].join(','),
    text: t('Subir un archivo'),
    inputRef: FileUploadRef,
    maxSize: "2",
    containerStyle: {
      width: isMobile ? '100%' : 265,
      height: 147
    },
    containerStylePending: {
      height: '100%'
    },
    containerStyleDone: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    containerStyleUploading: {
      height: '100%'
    },
    containerStyleFragment: {
      padding: '0 28px',
      height: '100%'
    }
  })))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: clearModalContent,
    disabled: isPending
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: updatePhoto,
    disabled: isDisabledUpdateButton || isPending
  }, t('Actualizar'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
/**
 * This component represents a modal for forgot password email.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} logo Logo src.
 * @param {boolean} hideBackParam Hides the back button of the modal.
 */

export function ForgotPasswordEmailSentModal({
  isVisible,
  setIsVisible,
  logo,
  hideBackParam
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 830,
    height: 272,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 830,
      height: 272
    })
  }, jsx("div", {
    css: [styles, {
      padding: '14px 39px 34px'
    }]
  }, jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(CloseIcon, {
    css: {
      width: 20,
      height: 20,
      cursor: 'pointer',
      color: '#CE1625'
    },
    onClick: setIsVisible
  })), jsx("div", {
    className: "modal-row",
    css: {
      [mediaQuery[0]]: {
        display: 'flex !important',
        flexDirection: 'column !important',
        marginBottom: '20px !important'
      }
    }
  }, jsx("div", {
    className: "modal-column",
    css: {
      [mediaQuery[0]]: {
        alignItems: 'flex-start !important'
      }
    }
  }, jsx("div", null, jsx("img", {
    src: logo,
    alt: "plane",
    css: {
      width: '100%',
      height: '100%',
      [mediaQuery[0]]: {
        width: '70%'
      }
    }
  }))), jsx("div", {
    className: "modal-column",
    style: {
      flexBasis: '60%'
    },
    css: {
      [mediaQuery[0]]: {
        alignItems: 'flex-start !important'
      }
    }
  }, jsx("h1", {
    className: "modal-title",
    style: {
      color: '#08415C'
    }
  }, t('¡Tiene un correo electrónico!')), jsx("p", {
    className: "modal-p"
  }, t('Revise su buzón de entrada')))), hideBackParam !== '1' && jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(Button, {
    style: {
      width: 172,
      height: 37,
      background: themes[theme].eta,
      color: themes[theme].beta,
      borderRadius: 6,
      border: 'none',
      padding: 0
    },
    onClick: () => history.push('/'),
    css: {
      [mediaQuery[0]]: {
        width: '100% !important'
      }
    }
  }, t('Ir al inicio')))));
}
/**
 * This component represents a modal for Usage policies.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} usagePolicies Usage policies.
 * @param {function} acceptTerms Accept terms.
 * @param {boolean} isPendingAcceptTerms Determines if the user accepted the terms or not.
 */

export function UsagePoliciesModal({
  isVisible,
  usagePolicies,
  acceptTerms,
  isPendingAcceptTerms,
  text = 'Estimado usuario, antes de iniciar el uso de la plataforma Tramite ¡YA! para la Dirección General de Migración y Extranjería, es necesario que lea y acepte las condiciones de uso de este portal',
  checkText = 'He leído y acepto las condiciones de uso de Dirección General de Migración y Extranjería'
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const [acceptTermsChecked, setAcceptTermsChecked] = React.useState(false);

  const onChangeAcceptTerms = ({
    target: {
      checked
    }
  }) => {
    setAcceptTermsChecked(checked);
  };

  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 363,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 363
    })
  }, jsx("div", {
    css: [styles, {
      padding: '32px 19px 14px 40px'
    }]
  }, jsx("div", null, jsx("h1", {
    className: "modals-title",
    css: {
      margin: '0 0 40px',
      color: `${themes[theme].delta} !important`,
      [mediaQuery[0]]: {
        fontSize: '22px !important'
      }
    }
  }, t('Condiciones de uso')), jsx("p", {
    className: "modals-description",
    style: {
      margin: '0 0 40px'
    }
  }, t(text)), jsx(Button, {
    onClick: () => {
      window.open(usagePolicies, '_blank');
    },
    style: {
      width: isMobile ? '100%' : 185,
      height: 37,
      color: themes[theme].beta,
      background: themes[theme].eta,
      borderRadius: 25,
      border: 'none',
      padding: 0,
      marginBottom: 20
    },
    spanStyle: {
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, jsx(DownloadAltIcon, {
    className: "icon",
    css: {
      width: '20px !important',
      height: '20px !important',
      marginRight: 10,
      color: `${themes[theme].beta} !important`
    }
  }), t('Descargar pdf')), jsx(SimpleCheckBox, {
    color: acceptTermsChecked ? 'fireEngineRed' : 'grey',
    checked: acceptTermsChecked,
    onChange: onChangeAcceptTerms,
    containerStyle: isMobile ? {
      marginBottom: 20
    } : {},
    css: {
      [mediaQuery[0]]: {
        width: '40px !important'
      }
    },
    text: `${t(checkText)}`
  })), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    disabled: !acceptTermsChecked,
    onClick: acceptTerms
  }, t('Aceptar'), isPendingAcceptTerms ? jsx(SpinnerSmall, {
    style: {
      marginLeft: 15,
      width: 20,
      height: 20
    }
  }) : ''))));
}
/**
 * This component represents a modal for alert reset password.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} user User representation.
 * @param {string} app App.
 * @param {strings} api Api.
 * @param {function} setNavigateToReset Function to reset navigation.
 */

export function AlertResetPasswordModal({
  isVisible,
  user,
  app,
  api,
  setNavigateToReset
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 44
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Cambiar contraseña')), jsx("p", {
    className: "modals-description"
  }, t('Está a punto de crear una nueva contraseña para su usuario')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => setNavigateToReset(false)
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => {
      window.location.href = `${process.env.REACT_APP_RESETPASS_URL}${user.email}?app=${app}&api=${api}&user=${user.id}&back=${window.location.href}`;
    }
  }, t('Continuar')))));
}
/**
 * This component represents a modal for Expired Password.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} passImg Password img src.
 */

export function ExpiredPasswordModal({
  isVisible,
  setIsVisible,
  passImg
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 830,
    height: 272,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 830,
      height: 272
    })
  }, jsx("div", {
    css: [styles, {
      padding: '55px 25px 19px 39px'
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("img", {
    className: "shield-logo",
    src: passImg,
    alt: "passImg"
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Su contraseña ha vencido')), jsx("p", {
    className: "modals-description"
  }, t('Por seguridad es necesario cambiar su contraseña, por favor revise el buzón de entrada de su correo electrónico y clickeé el link adjunto para realizar el cambio')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(false)
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for resend an email.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} resendEmail Sends the email.
 */

export function VerifyUserModal({
  isVisible,
  resendEmail
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 302,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 302
    })
  }, jsx("div", {
    css: [styles, {
      padding: '36px 19px 16px 30px'
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(MailIcon, {
    className: "icon",
    css: {
      width: '71px !important',
      height: '71px !important',
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Su cuenta no ha sido activada')), jsx("p", {
    className: "modals-description"
  }, t('El usuario indicado no ha confirmado su dirección de correo electrónico, por favor diríjase a su bandeja de entrada[ext]'), "."), jsx("p", {
    className: "modals-description"
  }, t('Una vez realizado este proceso podrá iniciar sesión y empezar sus trámites'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      width: 320,
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: resendEmail
  }, t('Reenviar correo de confirmación')))));
}
/**
 * This component represents a modal for confirm email.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 */

export function AlertConfirmEmailModal({
  isVisible,
  setIsVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 42
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Sus datos no coinciden')), jsx("p", {
    className: "modals-description"
  }, t('El correo electrónico principal indicado no coincide con la información, por favor revise que sean[ext]'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(false)
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for general errors.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} message Error message.
 */

export function AlertGeneralErrorModal({
  isVisible,
  setIsVisible,
  message = ''
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    }),
    className: "dont-close"
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, theme === 'phytosanitaryWeb' ? jsx(BallonIcon, {
    className: "traffic-cone"
  }) : jsx(TrafficConeIcon, {
    className: "traffic-cone"
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Lo sentimos')), jsx("p", {
    className: "modals-description"
  }, message || t('Se produjo un error al completar esta acción, por favor, inténtelo más tarde')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(false)
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for editing a procedure.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} path To navigate when close.
 */

export function EditProcedureModal({
  isVisible,
  setIsVisible,
  path
}) {
  const {
    theme
  } = useTheme();
  const history = useHistory();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 30
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(PencilCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Editar trámite')), jsx("p", {
    className: "modals-description"
  }, theme === 'migratoryWebCitizen' && t('Para realizar los cambios solicitados por la Dirección General de Migración y Extranjería se debe editar el trámite [ext]'), theme === 'sanitarianRegistersWeb' && t('Para realizar los cambios solicitados se debe editar el trámite [ext]')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => setIsVisible(false)
  }, t('Cancelar')), jsx(Button, {
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => {
      history.push(path);
    }
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for procedure information summary.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {object} data Procedure data.
 * @param {object} user User representation.
 * @param {boolean} isFunctionary Determines if the user is in migratory-web-citizen or operator-web.
 */

export function ProcedureInfoModal({
  isVisible,
  setIsVisible,
  data,
  user,
  isFunctionary,
  isFinalizeRevision
}) {
  var _data$ProcedureDefini, _data$ProcedureQRCode, _data$DigitalRecord, _user$digitalRecord, _data$Institution, _data$ProcedureQRCode2, _data$ProcedureQRCode3, _data$ProcedureQRCode4, _data$ProcedureQRCode5, _data$ProcedureQRCode6, _data$ProcedureQRCode7, _data$User, _data$RequesterDigita;

  const {
    theme
  } = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  const showValidityTime = (data === null || data === void 0 ? void 0 : (_data$ProcedureDefini = data.ProcedureDefinition) === null || _data$ProcedureDefini === void 0 ? void 0 : _data$ProcedureDefini.hasQR) && !!(data === null || data === void 0 ? void 0 : (_data$ProcedureQRCode = data.ProcedureQRCode) === null || _data$ProcedureQRCode === void 0 ? void 0 : _data$ProcedureQRCode.GeneratedQRCode);
  const recordCode = isFunctionary ? data === null || data === void 0 ? void 0 : (_data$DigitalRecord = data.DigitalRecord) === null || _data$DigitalRecord === void 0 ? void 0 : _data$DigitalRecord.code : user === null || user === void 0 ? void 0 : (_user$digitalRecord = user.digitalRecord) === null || _user$digitalRecord === void 0 ? void 0 : _user$digitalRecord.code;
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 550,
    onClose: () => {},
    customStyles: { ...modalCustomStyle({
        isMobile,
        width: 781,
        height: 550
      }),
      borderRadius: '20px'
    }
  }, jsx("div", {
    css: [styles, {
      padding: '40px 0px 30px 40px'
    }]
  }, jsx("div", null, jsx("div", {
    className: "scrollable"
  }, jsx(ScrollWrapper, {
    containerStyle: {
      minHeight: isMobile ? 300 : 420,
      marginBottom: 30
    }
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx("h1", {
    className: "modals-subtitle"
  }, t('Información del trámite'))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Número de trámite'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, data === null || data === void 0 ? void 0 : data.code))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Número de versión'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, data === null || data === void 0 ? void 0 : data.version))), jsx(React.Fragment, null, !isFinalizeRevision && jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Enviado por el ciudadano el'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, (data === null || data === void 0 ? void 0 : data.createdAtProcedure) && format(parseISO(data === null || data === void 0 ? void 0 : data.createdAtProcedure), 'dd/MM/yyyy h:mm aaaa')))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Modificado por última vez el'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, (data === null || data === void 0 ? void 0 : data.updatedAt) && format(parseISO(data === null || data === void 0 ? void 0 : data.updatedAt), 'dd/MM/yyyy h:mm aaaa')))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Institución ITV'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, (data === null || data === void 0 ? void 0 : (_data$Institution = data.Institution) === null || _data$Institution === void 0 ? void 0 : _data$Institution.name) || '-')))), showValidityTime && jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Período de vigencia'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, data === null || data === void 0 ? void 0 : (_data$ProcedureQRCode2 = data.ProcedureQRCode) === null || _data$ProcedureQRCode2 === void 0 ? void 0 : (_data$ProcedureQRCode3 = _data$ProcedureQRCode2.GeneratedQRCode) === null || _data$ProcedureQRCode3 === void 0 ? void 0 : _data$ProcedureQRCode3.validityTime))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Fecha de vencimiento'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, (data === null || data === void 0 ? void 0 : (_data$ProcedureQRCode4 = data.ProcedureQRCode) === null || _data$ProcedureQRCode4 === void 0 ? void 0 : (_data$ProcedureQRCode5 = _data$ProcedureQRCode4.GeneratedQRCode) === null || _data$ProcedureQRCode5 === void 0 ? void 0 : _data$ProcedureQRCode5.expiresAt) && format(parseISO(data === null || data === void 0 ? void 0 : (_data$ProcedureQRCode6 = data.ProcedureQRCode) === null || _data$ProcedureQRCode6 === void 0 ? void 0 : (_data$ProcedureQRCode7 = _data$ProcedureQRCode6.GeneratedQRCode) === null || _data$ProcedureQRCode7 === void 0 ? void 0 : _data$ProcedureQRCode7.expiresAt), 'dd/MM/yyyy h:mm aaaa')))))), jsx(DivRow, {
    className: "modals-row"
  }, jsx("h1", {
    className: "modals-subtitle"
  }, t('Información del tramitador'))), !isFunctionary ? jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Expediente del tramitador'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, recordCode))) : jsx(React.Fragment, null, isFinalizeRevision ? jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Nombre'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, data === null || data === void 0 ? void 0 : (_data$User = data.User) === null || _data$User === void 0 ? void 0 : _data$User.fullName))), (_data$RequesterDigita = data.RequesterDigitalRecord) === null || _data$RequesterDigita === void 0 ? void 0 : _data$RequesterDigita.ExternalDigitalRecords.map(externalRecord => {
    var _externalRecord$Exter;

    return jsx(DivRow, {
      className: "modals-row"
    }, jsx(DivColumn, {
      className: "modals-column-info"
    }, (_externalRecord$Exter = externalRecord.ExternalDigitalRecordType) === null || _externalRecord$Exter === void 0 ? void 0 : _externalRecord$Exter.description, ":"), jsx(DivColumn, {
      className: "modals-column-info"
    }, jsx("span", null, (externalRecord === null || externalRecord === void 0 ? void 0 : externalRecord.code.length) ? externalRecord === null || externalRecord === void 0 ? void 0 : externalRecord.code : '---')));
  })) : jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Expediente del tramitador'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, recordCode))))), jsx(DivRow, {
    className: "modals-row"
  }, jsx("h1", {
    className: "modals-subtitle"
  }, t('Información del solicitante'))), jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column-info"
  }, t('Nombre'), ":"), jsx(DivColumn, {
    className: "modals-column-info"
  }, jsx("span", null, data === null || data === void 0 ? void 0 : data.requesterName)))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta,
      width: 128
    },
    onClick: () => setIsVisible(false)
  }, t('Cerrar')))))));
}
/**
 * This component represents a billing info modal.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {object} formik Formik.
 * @param {function} onResetModalInfo Observation component.
 */

export function BillInfoModal({
  isVisible,
  setIsVisible,
  formik = null,
  onResetModalInfo
}) {
  const {
    theme
  } = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  const [identifications, setIdentifications] = React.useState([]);
  useIdentificationsTypes({
    scope: '',
    config: {
      onSuccess(data) {
        const dataFiltered = data === null || data === void 0 ? void 0 : data.filter(({
          code
        }) => !['safePassage', 'neighborhoodPermit', 'lawyerCard', 'none', 'travelDocument'].includes(code));
        const idTypes = dataToArray(dataFiltered, [{
          text: 'name'
        }, {
          value: 'id'
        }, {
          code: 'code'
        }]);
        setIdentifications(idTypes);
      }

    }
  });

  const maxLengthBasedOnIdentificationTypes = identificationCode => {
    switch (identificationCode) {
      case 'dimex':
        return 12;

      case 'identification':
        return 9;

      case 'nite':
      case 'legalIdentification':
        return 10;

      case 'passport':
        return 20;

      default:
        return 9;
    }
  };

  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 600,
    onClose: () => {},
    customStyles: { ...modalCustomStyle({
        isMobile,
        width: 781,
        height: 600
      }),
      borderRadius: '20px'
    }
  }, jsx("form", {
    css: [styles],
    onSubmit: formik.handleSubmit
  }, jsx("div", null, jsx("div", {
    className: "scrollable"
  }, jsx(ScrollWrapper, {
    containerStyle: {
      minHeight: isMobile ? 300 : 420,
      marginBottom: 30
    }
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].gamma
    }
  }, t('Datos de facturación'))), jsx(AlertRequiredFields, {
    customText: t('Señor usuario si desea que la factura electrónica le llegue a otra persona o entidad necesitamos su consentimiento, favor ingresar los siguientes datos')
  }), jsx(FormDiv, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Tipo de identificación'), ": *"), jsx(ButtonSelector, {
    name: "typeId",
    value: formik.values.typeId,
    data: identifications,
    onChange: async item => {
      await onResetModalInfo();
      const dataIdentification = identifications.filter(({
        value
      }) => value === item).map(datas => ({
        id: datas.value,
        code: datas.code,
        name: datas.text
      }));
      formik.setFieldValue('typeId', item);
      formik.setFieldValue('identificationName', dataIdentification[0].name);
      formik.setFieldValue('identificationMaxLength', maxLengthBasedOnIdentificationTypes(dataIdentification[0].code));
    },
    css: {
      width: '215px !important'
    }
  }), jsx(FormError, {
    msg: formik.errors.typeId,
    touched: formik.touched.typeId
  })), jsx(FormDiv, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Número de identificación'), ": *"), jsx(InputText, {
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    name: "identification",
    onChange: formik.handleChange,
    value: formik.values.identification,
    error: formik.errors.identification,
    touched: formik.touched.identification,
    width: "90%",
    maxLength: formik.values.identificationMaxLength
  }), jsx(FormError, {
    msg: formik.errors.identification,
    touched: formik.touched.identification
  })), jsx(FormDiv, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Nombre completo'), ": *"), jsx(InputText, {
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    name: "name",
    onChange: formik.handleChange,
    value: formik.values.name,
    error: formik.errors.name,
    touched: formik.touched.name,
    width: "90%",
    maxLength: 255
  }), jsx(FormError, {
    msg: formik.errors.name,
    touched: formik.touched.name
  })), jsx(FormDiv, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Correo electrónico'), ": *"), jsx(InputText, {
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    name: "email",
    onChange: formik.handleChange,
    value: formik.values.email,
    error: formik.errors.email,
    touched: formik.touched.email,
    width: "90%",
    maxLength: 128
  }), jsx(FormError, {
    msg: formik.errors.email,
    touched: formik.touched.email
  }))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => {
      setIsVisible(false);
    }
  }, t('Cancelar')), jsx(Button, {
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    type: "submit"
  }, t('Guardar datos')))))));
}
/**
 * This component represents a modal observations.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {object} disclaimer Disclaimer data.
 * @param {*} ObservationDetail Observation component.
 */

export function ObservationModal({
  isVisible,
  setIsVisible,
  disclaimer,
  ObservationDetail
}) {
  const {
    theme
  } = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 533,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 533
    })
  }, jsx("div", {
    css: [styles, {
      padding: '32px 22px 12px 40px'
    }]
  }, jsx("div", {
    className: "observations-modal"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].delta
    }
  }, t('Estimado usuario')), jsx("p", {
    className: "modals-description"
  }, t('observacion_tramite_descripcion_modal')), jsx("p", {
    className: "modals-description"
  }, t('Agradecemos su colaboración'), "."), jsx("div", {
    className: "scrollable"
  }, jsx(ScrollWrapper, {
    containerStyle: {
      minHeight: isMobile ? 200 : 260,
      paddingRight: 8
    }
  }, jsx("div", {
    style: {
      padding: '36px 44px'
    }
  }, disclaimer && jsx("div", null, disclaimer.messageJSON ? jsx(ObservationDetail, {
    data: disclaimer.messageJSON,
    t: t,
    isModal: true
  }) : disclaimer.message)))), jsx("div", {
    style: {
      marginBottom: 12
    }
  }, jsx("span", {
    className: "observation-footer-note"
  }, t('nota_observacion_modal'))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => setIsVisible(false)
  }, t('Cerrar'))))));
}
/**
 * This component represents a modal for answer a disclaimer.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} t Translation.
 * @param {object} disclaimer Disclaimer data.
 */

const initialValues = {
  disclaimer: '',
  files: [],
  error: ''
};
export default function AnswerDisclaimerModal({
  isVisible,
  setIsVisible,
  t,
  disclaimer
}) {
  var _disclaimer$Document, _disclaimer$Document2, _disclaimer$Document3;

  const {
    theme
  } = useTheme();
  const [state, setState] = React.useReducer(reducer, initialValues);
  const queryCache = useQueryCache();
  const {
    isMobile
  } = useMediaQuery();
  const onExit = React.useCallback(() => {
    setIsVisible();
    setState(initialValues);
  }, [setIsVisible]);
  const [answerDisclaimer, {
    isLoading: isPendingDisclaimer
  }] = useMutation(async () => {
    if (state.disclaimer.length === 0) {
      setState({
        error: 'La respuesta es requerida.'
      });
      throw new Error('Respuesta Requerida');
    }

    await client(`/procedures/${disclaimer.procedureId}/disclaimers`, {
      body: {
        message: state.disclaimer,
        attachments: queryCache.getQueryData('files'),
        previousObservation: disclaimer.id
      },
      method: 'POST'
    });
  }, {
    onSettled() {
      queryCache.invalidateQueries(['disclaimers', {
        procedureId: disclaimer.procedureId.toString()
      }], {
        throwOnError: true
      });
    },

    onSuccess() {
      onExit();
    }

  });
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 608,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 608
    })
  }, jsx("div", {
    css: [styles, {
      padding: '22px 16px 22px 35px'
    }]
  }, jsx("div", {
    className: "disclaimers-modal"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].delta
    }
  }, t('Nota aclaratoria')), jsx("div", {
    className: "modals-row-disclaimer two-columns"
  }, jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "title-span"
  }, t('Fecha'), ":"), jsx("span", {
    className: "plain-text"
  }, format(new Date(), 'dd/MM/yyyy'))), jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "title-span"
  }, t('Hora'), ":"), jsx("span", {
    className: "plain-text"
  }, format(new Date(), 'h:mm aa')))), jsx("div", {
    className: "modals-row-disclaimer"
  }, jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "title-span"
  }, t('Comentario'), ":"), jsx("span", {
    className: "plain-text"
  }, (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.messageJSON) || (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.message)))), jsx("div", {
    className: "modals-row-disclaimer"
  }, jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "title-span"
  }, t('Archivos adjuntos'), ":"), (disclaimer === null || disclaimer === void 0 ? void 0 : (_disclaimer$Document = disclaimer.Document) === null || _disclaimer$Document === void 0 ? void 0 : _disclaimer$Document.attachments.length) > 0 ? disclaimer === null || disclaimer === void 0 ? void 0 : (_disclaimer$Document2 = disclaimer.Document) === null || _disclaimer$Document2 === void 0 ? void 0 : (_disclaimer$Document3 = _disclaimer$Document2.attachments) === null || _disclaimer$Document3 === void 0 ? void 0 : _disclaimer$Document3.map(({
    id: attachmentId,
    relativePath,
    path
  }) => jsx(NotesInfoDocument, {
    key: attachmentId
  }, jsx("span", {
    className: "plain-text"
  }, relativePath), jsx(DownloadAltIcon, {
    className: "download-icon",
    css: {
      color: themes[theme].zeta
    },
    onClick: () => handleDownloadSingleFile(path)
  }))) : jsx("span", {
    className: "plain-text"
  }, t('No hay')))), jsx("div", {
    className: "modals-row-disclaimer",
    style: {
      marginBottom: 20
    }
  }, jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "plain-text",
    style: {
      marginBottom: 5
    }
  }, t('Máximo 200 caracteres'), ":"), jsx(TextArea, {
    rows: isMobile ? 2 : 4,
    autoComplete: "off",
    placeholder: t('Digite aquí su respuesta'),
    showCount: true,
    width: "100%",
    maxLength: "200",
    value: state.disclaimer,
    onChange: answer => setState({
      disclaimer: answer,
      error: ''
    })
  }), jsx("span", {
    style: {
      color: '#CE1625',
      fontSize: 12
    }
  }, state.error))), jsx("div", {
    className: "modals-row-disclaimer"
  }, jsx("div", {
    className: "modals-column-disclaimer"
  }, jsx("span", {
    className: "plain-text",
    css: {
      marginTop: 25,
      [mediaQuery[0]]: {
        marginTop: 0,
        marginBottom: 5
      }
    }
  }, t('De ser necesario, puede adjuntar documentos o imágenes'), ":")), jsx("div", {
    className: "modals-column-disclaimer file",
    style: isMobile ? {} : {
      flexBasis: '40%'
    }
  }, isMobile ? jsx(MultiFileUploader, {
    isVisible: isVisible,
    extensions: ['.doc', '.docx', '.pdf', '.jpg', '.jpeg', '.png'],
    t: t
  }) : jsx(ScrollWrapper, {
    containerStyle: {
      minHeight: 160,
      paddingRight: 8
    }
  }, jsx(MultiFileUploader, {
    isVisible: isVisible,
    extensions: ['.doc', '.docx', '.pdf', '.jpg', '.jpeg', '.png'],
    t: t
  })))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: onExit,
    disabled: isPendingDisclaimer
  }, t('Salir')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    disabled: isPendingDisclaimer,
    onClick: answerDisclaimer
  }, t('Enviar'), isPendingDisclaimer ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : '')))));
}
/**
 * This component represents a modal for people data not found.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 */

export function NotFoundInCensusModal({
  isVisible,
  setIsVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Consulta en registro nacional')), jsx("p", {
    className: "modals-description"
  }, t('No se encontró ninguna información con el número de identificación ingresado Por favor verifíquelo e inténtelo de nuevo, o si lo desea, puede continuar el trámite ingresando los datos de forma manual')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: setIsVisible
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for change the state of a qr code of procedure.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} procedureState Procedure qr state.
 * @param {function} modalAction Modal action.
 * @param {string} justification Change state justification.
 * @param {function} setJustification Sets the justification.
 */

const initialValue = {
  error: ''
};
export function ChangeProcedureCodeStateModal({
  isVisible,
  setIsVisible,
  procedureState,
  modalAction,
  justification,
  setJustification
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const [state, setState] = React.useReducer(reducer, initialValue);

  const changeProcedureState = () => {
    try {
      if (justification.length === 0) {
        setState({
          error: 'La justificación es requerida.'
        });
        throw new Error('La justificación es Requerida');
      }

      modalAction();
      setIsVisible(false);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    setJustification('');
  }, []);
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 882,
    height: 432,
    closeOnEsc: true,
    onClose: () => {
      setState(initialValues);
    }
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].iota,
      display: 'flex',
      justifyContent: 'flex-start'
    }
  }, procedureState === 'locked' ? 'DESBLOQUEAR CÓDIGO' : 'BLOQUEAR CÓDIGO'), jsx("div", {
    className: "row disclaimer",
    style: {
      marginBottom: 20
    }
  }, jsx("div", {
    className: "column"
  }, jsx(FormLabel, {
    text: "Justificaci\xF3n del cambio de estado (M\xE1ximo 200 caracteres):",
    styles: {
      marginBottom: '0px'
    },
    containerStyle: {
      display: 'flex',
      color: '#0D1F2D'
    }
  }), jsx("div", {
    style: {
      textAlign: 'left'
    }
  }, jsx(TextArea, {
    autoComplete: "off",
    placeholder: "Ingresar",
    width: "100%",
    maxLength: "200",
    style: {
      height: '170px',
      fontSize: '14px !important'
    },
    value: justification,
    onChange: justify => {
      if (state.error.length !== 0) {
        setState({
          error: ''
        });
      }

      setJustification(justify);
    }
  }), jsx("span", {
    style: {
      color: '#CE1625',
      fontSize: 12,
      marginLeft: '10px'
    }
  }, state.error)))))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    outline: true,
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`,
      width: '135px'
    },
    className: "button blue",
    onClick: () => setIsVisible(false)
  }, t('Cancelar')), jsx(Button, {
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta,
      width: '188px'
    },
    onClick: changeProcedureState
  }, procedureState === 'locked' ? `DESBLOQUEAR` : `BLOQUEAR`))));
}
/**
 * This component represents a modal for view the state of a qr code of procedure.
 * @param {string} title Modal title.
 * @param {string} description Modal description.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {string} procedureState Procedure qr state.
 * @param {object} data Procedure detail data.
 * @param {function} modalAction Modal action.
 */

export function ProcedureCodeStateModal({
  title,
  description,
  isVisible,
  setIsVisible,
  procedureState,
  data,
  modalAction
}) {
  var _data$DigitalRecord2, _data$ProcedureDefini2;

  function capitalize(str) {
    return str === null || str === void 0 ? void 0 : str.replace(/\w\S*/g, txt => {
      return (txt === null || txt === void 0 ? void 0 : txt.charAt(0).toUpperCase()) + txt.substr(1).toLowerCase();
    });
  }

  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 850,
    height: title ? 355 : 280,
    onClose: () => {}
  }, jsx("div", {
    style: {
      padding: '38px'
    }
  }, jsx("div", {
    className: "modal-row",
    style: {
      margiBottom: '20px'
    }
  }, jsx("div", null, jsx("div", {
    style: {
      display: 'flex',
      alignItems: 'start'
    }
  }, procedureState === 'locked' ? jsx(CircleLockIcon, {
    css: {
      width: '74px !important',
      height: '70px !important',
      marginRight: 20,
      color: '#0672AB'
    }
  }) : jsx(CircleUnlockIcon, {
    css: {
      width: '74px !important',
      height: '70px !important',
      marginRight: 20,
      color: '#0672AB'
    }
  }), jsx("div", {
    style: {
      width: '100%',
      marginBottom: '30px'
    }
  }, jsx("h1", {
    style: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '28px',
      color: '#0D1F2D',
      textAlign: 'left',
      marginBottom: '2px'
    }
  }, title || (procedureState === 'locked' ? 'EL CÓDIGO SE HA BLOQUEADO' : 'EL CÓDIGO SE HA DESBLOQUEADO')), jsx("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between'
    }
  }, jsx("div", {
    style: {
      textAlign: 'initial',
      marginRight: '15px'
    }
  }, jsx("p", {
    className: "code-modal-p"
  }, jsx("strong", null, "Nombre:"), ` ${capitalize(data === null || data === void 0 ? void 0 : (_data$DigitalRecord2 = data.DigitalRecord) === null || _data$DigitalRecord2 === void 0 ? void 0 : _data$DigitalRecord2.name)}`), jsx("p", {
    className: "code-modal-p",
    style: {
      marginTop: '2px'
    }
  }, jsx("strong", null, "Identificaci\xF3n:"), ` ${data === null || data === void 0 ? void 0 : data.requesterIdentification}`)), jsx("div", {
    style: {
      textAlign: 'initial'
    }
  }, jsx("p", {
    className: "code-modal-p"
  }, jsx("strong", null, "Tr\xE1mite:"), ` ${data === null || data === void 0 ? void 0 : (_data$ProcedureDefini2 = data.ProcedureDefinition) === null || _data$ProcedureDefini2 === void 0 ? void 0 : _data$ProcedureDefini2.name}`), jsx("p", {
    className: "code-modal-p",
    style: {
      marginTop: '2px'
    }
  }, jsx("strong", null, "N\xFAmero de tr\xE1mite:"), ` ${data === null || data === void 0 ? void 0 : data.code}`))), description && jsx("p", {
    className: "",
    style: {
      textAlign: 'left'
    }
  }, description))))), jsx("div", {
    style: {
      textAlign: 'right'
    }
  }, jsx(Button, {
    style: {
      width: 128,
      color: '#FFFFFF',
      background: '#CE1625',
      border: 'none',
      borderRadius: 6
    },
    onClick: () => {
      setIsVisible(false);

      if (modalAction) {
        modalAction();
      }
    }
  }, "ACEPTAR"))));
}
/**
 * This component represents a modal for presenting not found results.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {string} modalTitle Modal title.
 * @param {string} modalDescription Modal description.
 */

export function NotFoundGenericModal({
  isVisible,
  setIsVisible,
  modalTitle,
  modalDescription,
  CustomIcon = null
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column",
    style: {
      justifyContent: 'flex-start'
    }
  }, CustomIcon !== null && CustomIcon !== void 0 ? CustomIcon : jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, modalTitle), jsx("p", {
    className: "modals-description"
  }, modalDescription))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: setIsVisible
  }, t('Aceptar')))));
}
/**
 * This component represents a modal for Tax Class Not Found.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal.
 * @param {string} modalTitle Modal title.
 * @param {string} modalDescription Modal description.
 * @param {function} callback Function triggered when accept the modal action.
 */

export function TaxClassNotFoundModal({
  isVisible,
  setIsVisible,
  callback
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 30
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Clase tributaria no encontrada')), jsx("p", {
    className: "modals-description"
  }, t('La clase tributaria ingresada no fue encontrada [ext]'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(React.Fragment, null, jsx(Button, {
    outline: true,
    size: "md-icon",
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: () => {
      setIsVisible(false);

      if (callback) {
        callback({});
        return;
      } // TODO: VALIDAR LA URL PARA EL PUSH


      history.push('/');
    }
  }, t('Salir del trámite')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    spanStyle: {
      fontSize: 14
    },
    onClick: () => {
      setIsVisible(false);
      console.log('PUSH AL TRAMITE');
    }
  }, t('Trámites de clase tributaria'))))));
}
/**
 * This component represents a modal when the entity has debt with hacienda.
 * @param {boolean} isModalVisible Determines if the modal is visible or not.
 * @param {function} setIsModalVisible Function triggered when close the modal.
 */

export function EntityHasDebtModal({
  action,
  isModalVisible,
  setIsModalVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isModalVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    customStyles: modalCustomStyle({
      isMobile
    }),
    onClose: () => {}
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('LA EMPRESA SE ENCUENTRA MOROSA')), jsx("p", {
    className: "modals-description"
  }, t('Para poder continuar con este trámite es necesario resolver los problemas de morosidad que tiene la empresa consultada.')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    onClick: () => setIsModalVisible(false)
  }, t('VOLVER')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: action
  }, t('SALIR DEL TRÁMITE')))));
}
/**
 * This component represents a modal when the entity has debt with hacienda.
 * @param {boolean} isModalVisible Determines if the modal is visible or not.
 * @param {function} setIsModalVisible Function triggered when close the modal.
 */

export function ImportRecordCreatedModal({
  action,
  isModalVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isModalVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    customStyles: modalCustomStyle({
      isMobile
    }),
    onClose: () => {}
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Expediente de importación creado')), jsx("p", {
    className: "modals-description"
  }, t('Ahora puede añadir vehículos a este registro'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: action
  }, t('Aceptar')))));
}
export function AutorizedCreatedModal({
  isVisible,
  setIsVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Autorizado Agregado')), jsx("p", {
    className: "modals-description"
  }, t('El registro del nuevo autorizado se realizó exitosamente.')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: setIsVisible
  }, t('Aceptar')))));
}
export function AutorizedEditedModal({
  isVisible,
  setIsVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 28
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Información guardada')), jsx("p", {
    className: "modals-description"
  }, t('Se han modificado los datos exitosamente.')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red upper",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: setIsVisible
  }, t('Aceptar')))));
}
export function DeleteAuthorized({
  isVisible,
  onCloseModal,
  isPending,
  onConfirm
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Eliminar autorizado')), jsx("p", {
    className: "modals-description"
  }, t('Está seguro que desea eliminar este autorizado Al eliminarlo no podrá recuperar la información')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    disabled: isPending,
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    className: "button blue",
    onClick: onCloseModal
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    disabled: isPending,
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onConfirm
  }, t('Aceptar'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
export function AuthorizedDeleted({
  isVisible,
  onCloseModal
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(CheckCircleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Autorizado eliminado')), jsx("p", {
    className: "modals-description"
  }, t('El autorizado seleccionado fue eliminado exitosamente')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onCloseModal
  }, t('Aceptar')))));
}
export function AuthorizedUserNotFound({
  isVisible,
  onCloseModal
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('La persona no se encuentra registrada')), jsx("p", {
    className: "modals-description"
  }, t('El número de identificación indicado no pertenece a ninguna persona registrada  en la plataforma, por favor, revise el número de identificación.')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onCloseModal
  }, t('Aceptar')))));
}
export function AuthorizedUserAlreadyRegistered({
  isVisible,
  onBack,
  onRedirect
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    css: {
      color: themes[theme].zeta
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('La persona ya se encuentra agregada')), jsx("p", {
    className: "modals-description"
  }, t('El número de identificación que ingreso pertenece a una persona que ya fue agregada a este expediente.')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    style: {
      color: themes[theme].dseta,
      border: `1px solid ${themes[theme].dseta}`
    },
    className: "button blue",
    onClick: onBack
  }, t('Volver')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: onRedirect
  }, t('Ir al inicio')))));
}
export function DigitalRecordUpdates({
  isVisible,
  children
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      height: '627px'
    })
  }, jsx("div", {
    css: styles
  }, jsx(Typography, {
    variant: "h4"
  }, t('Cambiar estado')), jsx("p", {
    css: css`
            font-style: normal;
            font-weight: normal;
            font-size: 14px;
            line-height: 20px;
            color: #333333;
            margin: 0 0 30px 0;
          `
  }, t('Por favor seleccione el estado que desea asignar, ingrese un motivo y proceda a firmarlo digitalmente. ')), children));
}
export function GenericModal({
  children,
  ...props
}) {
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, _extends({
    visible: true,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, props), jsx("div", {
    css: css`
          width: 100%;
          height: 100%;
          box-sizing: border-box;
        `
  }, children));
}
export function GenericAdviceModal({
  icon,
  isVisible = true,
  title = '',
  description,
  buttons
}) {
  const {
    isMobile
  } = useMediaQuery();
  const {
    theme
  } = useTheme();
  const iconProps = {
    css: {
      color: themes[theme].zeta
    },
    className: 'icon'
  };
  const titleProps = {
    className: 'modals-title',
    style: {
      color: themes[theme].epsilon
    },
    css: css`
      align-self: flex-start;
      margin: 0 !important;
    `
  };
  const descriptionProps = {
    className: 'modals-description',
    css: css`
      align-self: flex-start;
      text-align: left;
      margin: 12px 0 0 0;
    `
  };
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile
    })
  }, jsx("div", {
    css: styles
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, icon ? cloneElement(icon, iconProps) : jsx(ExclamationTriangleIcon, iconProps)), jsx(DivColumn, {
    className: "modals-column"
  }, /*#__PURE__*/React.isValidElement(title) ? cloneElement(title, titleProps) : jsx("h1", titleProps, title), /*#__PURE__*/React.isValidElement(description) ? cloneElement(description, descriptionProps) : jsx("p", descriptionProps, description))), jsx("div", {
    className: "button-container"
  }, buttons)));
}
/**
 * This component represents a modal when the entity has debt with hacienda.
 * @param {boolean} isVisible Determines if the modal is visible or not.
 */

export function UsageRedirectModifyBussinesData({
  isVisible
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 781,
    height: 255,
    customStyles: modalCustomStyle({
      isMobile
    }),
    onClose: () => {}
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    className: "icon",
    color: themes[theme].zeta
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('DEBE COMPLETAR LOS DATOS DEL PERFIL')), jsx("p", {
    className: "modals-description"
  }, t('Para poder continuar con este trámite es necesario que complete los datos del perfil de la empresa'), "."))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    color: "cerulean",
    className: "button",
    outline: true,
    style: {
      width: 180,
      fontSize: 16,
      background: 'transparent',
      marginRight: 9,
      color: '#0F7CB5'
    },
    onClick: () => history.push('/?view=newProcedure')
  }, t('Volver')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta,
      width: 260
    },
    onClick: () => history.push('/profile/edit')
  }, t('Completar datos del perfil')))));
}
/**
 * This component represents a modal when the procedure can not be edited.
 * @param {boolean} isModalVisible Determines if the modal is visible or not.
 * @param {function} addEconomicActivity Function to send the user information
 * @param {function} setIsModalVisible Function triggered when close the modal.
 * @param {boolean} isPending Determines if there are a proccess pending.
 */

export function CannotHaveEconomicActivityModal({
  isModalVisible,
  setIsModalVisible,
  isPending = false,
  addEconomicActivity
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isMobile
  } = useMediaQuery();
  return jsx(Rodal, {
    visible: isModalVisible,
    showCloseButton: false,
    width: 830,
    height: 526,
    borderRadius: 20,
    closeOnEsc: true,
    onClose: () => {},
    customStyles: modalCustomStyle({
      isMobile,
      width: 781,
      height: 295,
      borderRadius: 20
    })
  }, jsx("div", {
    css: [styles, {
      paddingTop: 48
    }]
  }, jsx(DivRow, {
    className: "modals-row"
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx(ExclamationTriangleIcon, {
    css: {
      color: themes[theme].alpha
    }
  })), jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      color: themes[theme].epsilon
    }
  }, t('Atención')), jsx("p", {
    className: "modals-description"
  }, t('No se cuenta con una actividad económica registrada')))), jsx("div", {
    className: "button-container"
  }, jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].alpha,
      color: themes[theme].beta
    },
    onClick: () => {
      setIsModalVisible(false);
      addEconomicActivity();
    }
  }, t('Agregar actividad económica'), isPending ? jsx(SpinnerSmall, {
    className: "spinner"
  }) : ''))));
}
const styles = css`
  padding: 54px 19px 19px 28px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  box-sizing: border-box;
  ${mediaQuery[0]} {
    padding: 20px;
  }

  .modals-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 42px;

    ${mediaQuery[0]} {
      display: flex !important;
      flex-direction: column !important;
      margin-bottom: 20px !important;
    }
  }

  .modals-column,
  column-profile {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    justify-content: center;

    ${mediaQuery[0]} {
      align-items: flex-start !important;
    }
  }

  .modals-column {
    :last-of-type {
      flex-basis: 65% !important;
      align-items: unset;
      justify-content: unset;
    }
  }

  .modals-column-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: flex-start;
    padding-bottom: 15px;
    border-bottom: 1px solid #0672ab;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.01em;
    ${mediaQuery[0]} {
      border: none;
    }
    span {
      font-weight: 600;
    }
  }

  .icon {
    width: 63px;
    height: 63px;
    color: #0f7cb5;
    ${mediaQuery[0]} {
      width: 65px !important;
      height: 58px !important;
    }
  }

  .signature-logo {
    width: 98px;
    height: 98px;
  }

  .shield-logo {
    width: 104px;
    height: 104px;
  }

  .traffic-cone {
    width: 176px;
    height: 140px;
    color: #0f7cb5;
    ${mediaQuery[0]} {
      width: 65px !important;
      height: 58px !important;
    }
  }

  .modals-title {
    font-style: normal;
    font-weight: bold;
    font-size: 24px;
    margin-top: 10px;
    color: #08415c;
    text-transform: uppercase;
  }

  .modals-subtitle {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.05em;
    color: #0672ab;
    text-transform: uppercase;
  }

  .modal-p {
    margin: 0;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 25px;
    color: #333333;
  }

  .code-modal-p {
    margin-bottom: 0px;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 25px;
    color: #333333;
  }

  .modals-description {
    color: #333333;
    font-size: 14px;

    ${mediaQuery[0]} {
      font-size: 14px !important;
      line-height: 20px !important;
    }
  }

  .button-container {
    display: flex;
    justify-content: flex-end;
    ${mediaQuery[0]} {
      flex-wrap: wrap !important;
    }

    .button {
      width: 238px;
      height: 45px;
      color: #ffffff;
      border-radius: 6px;
      padding: 12px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-transform: uppercase !important;
      ${mediaQuery[0]} {
        width: 100% !important;
      }

      span {
        font-size: 14px;

        ${mediaQuery[0]} {
          font-size: 12px;
        }
      }
    }

    .red {
      width: 238px;
      height: 45px;
      border: none;
      ${mediaQuery[0]} {
        margin-bottom: 10px !important;
      }
    }

    .blue {
      width: 238px;
      height: 45px;
      background: transparent;
      margin-right: 9px;
      color: #0f7cb5;
      border-radius: 6px;
      ${mediaQuery[0]} {
        margin-right: 0 !important;
        order: 2 !important;
      }
    }

    .red2 {
      width: auto;
      padding: 0 10px;
      height: 37px;
      background: transparent;
      margin-right: 9px;
      color: #ce1625;
      border-radius: 6px;
      ${mediaQuery[0]} {
        margin-right: 0 !important;
        order: 2 !important;
        height: 45px;
      }
    }

    .blue2 {
      width: 214px;
      height: 37px;
      border: none;
      ${mediaQuery[0]} {
        margin-bottom: 10px !important;
      }
    }

    .upper {
      text-transform: uppercase;
    }

    .spinner {
      margin-left: 5px;
      width: 20px;
      height: 20px;
    }
  }

  .observations-modal {
    .scrollable {
      background-color: #ebf1f4;
      border-radius: 12px;
      margin-bottom: 18px;
    }

    .observation-footer-note {
      font-style: italic;
      font-weight: normal;
      font-size: 12px;
      color: #333333;
      margin-bottom: 12px;
    }
  }

  .disclaimers-modal {
    .title-span {
      font-style: normal;
      font-weight: bold;
      font-size: 13px;
      text-transform: uppercase;
      color: #2b2d42;
    }

    .plain-text {
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      color: #333333;
    }

    .modals-row-disclaimer,
    .row {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin-bottom: 10px;
      ${mediaQuery[0]} {
        flex-direction: column;
      }
    }

    .modals-column-disclaimer,
    .column {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .row {
      margin-bottom: 22px;
    }

    .column {
      :last-of-type {
        flex-basis: 65%;
      }
    }

    .modals-column-disclaimer.file {
      ${mediaQuery[0]} {
        min-height: 130px;
        overflow: auto;
        height: 100px;
        flex: unset;
      }
    }

    .modals-row-disclaimer.two-columns {
      ${mediaQuery[0]} {
        flex-direction: row;
      }

      .modals-column-disclaimer {
        :last-of-type {
          flex-basis: 65%;
        }

        ${mediaQuery[0]} {
          :last-of-type {
            flex-basis: 0;
          }
        }
      }
    }

    textarea {
      background: #ffffff;
      box-shadow: 0px 7px 64px rgba(0, 0, 0, 0.07);
      border-radius: 6px;
      border: none;
      min-height: 100px;
      ${mediaQuery[0]} {
        min-height: 50px;
      }
    }
  }
`;